/**
 * Turning a group of tiles as one.
 *
 * Shared between the server, which does it to the world, and the editor, which
 * has to know where everything ended up so it can keep hold of it. Written down
 * once, because two copies of this would drift apart and the editor would let go
 * of things it had only just turned.
 */

export interface TilePoint {
  tx: number;
  tz: number;
}

/** The middle of a group of tiles, which is what turning it happens around. */
export function pivotOf(tiles: readonly TilePoint[]): TilePoint {
  if (tiles.length === 0) return { tx: 0, tz: 0 };

  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;

  for (const tile of tiles) {
    minX = Math.min(minX, tile.tx);
    maxX = Math.max(maxX, tile.tx);
    minZ = Math.min(minZ, tile.tz);
    maxZ = Math.max(maxZ, tile.tz);
  }

  return { tx: Math.floor((minX + maxX) / 2), tz: Math.floor((minZ + maxZ) / 2) };
}

/** Where a tile ends up after the group it belongs to is turned. */
export function turnAround(centre: TilePoint, tx: number, tz: number, turns: number): TilePoint {
  let x = tx - centre.tx;
  let z = tz - centre.tz;

  for (let turn = 0; turn < (turns & 3); turn++) {
    const held = x;
    x = z;
    z = -held;
  }

  return { tx: centre.tx + x, tz: centre.tz + z };
}

/**
 * How one thing is turned and sized when a group is varied.
 *
 * Worked out from where it stands and a number the editor picks, so both sides
 * arrive at the same answer without the result having to be sent back. Pressing
 * the button again picks another number and gives another arrangement.
 */
export function variationOf(
  seed: number,
  tx: number,
  tz: number,
): { turns: number; scale: number } {
  // A small integer hash. Nothing here needs to be unguessable, only spread out
  // enough that neighbours do not come out the same.
  let hash = (seed * 374_761_393 + tx * 668_265_263 + tz * 2_246_822_519) | 0;
  hash = Math.imul(hash ^ (hash >>> 13), 1_274_126_177);
  hash = (hash ^ (hash >>> 16)) >>> 0;

  // Sizes land on sixteenths, which is all the wire carries: a size the file
  // remembers and the world cannot draw is a difference waiting to be noticed.
  return {
    turns: hash & 3,
    scale: Math.round((0.85 + ((hash >>> 2) % 36) / 100) * 16) / 16,
  };
}
